import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import { useTranslation } from 'react-i18next';
import { mainApi } from '../../config/apiClient';
import { useAuth } from '../../contexts/AuthContext';

type Environment = 'test' | 'live';

interface CourierIntegration {
  id: number;
  provider: string;
  branchId: string;
  branchName?: string | null;
  environment: Environment;
  storeId: string;
  hasOwnToken: boolean;
  isEnabled: boolean;
  lastTestAt?: string | null;
  lastTestResult?: string | null;
  lastTestOk?: boolean | null;
  lastEventAt?: string | null;
  updatedAt?: string | null;
}

interface CourierIntegrationList {
  sharedTokenConfigured: boolean;
  webhookConfigured: boolean;
  integrations: CourierIntegration[];
}

interface FiyuuWebhookInfo {
  configured: boolean;
  url?: string | null;
  statuses: string[];
  method: string;
}

interface IntegrationEvent {
  id: number;
  externalOrderId?: string | null;
  status?: string | null;
  riderName?: string | null;
  source: string;
  receivedAt: string;
  result: string;
  note?: string | null;
  payloadJson?: string | null;
}

interface OpenOrder {
  deliveryId: number;
  externalOrderId: string;
  orderNumber?: string | null;
  orderSource: string;
  status: string;
  externalStatus?: string | null;
  riderName?: string | null;
  assignedAt: string;
}

const SIM_STATUSES = ['order-created', 'order-assigned', 'order-shipped', 'order-delivered', 'order-canceled'];

interface Draft {
  id?: number;
  provider: string;
  branchId: string;
  environment: Environment;
  storeId: string;
  token: string;
  clearToken: boolean;
  hasOwnToken: boolean;
  isEnabled: boolean;
}

const BASE = '/api/CourierIntegrations';

async function call<T>(p: Promise<any>): Promise<T> {
  try {
    const res = await p;
    if (res.data?.success === false) throw new Error(res.data?.message || 'İşlem yapılamadı');
    return res.data?.data as T;
  } catch (err: any) {
    throw new Error(err?.response?.data?.message || err?.message || 'İşlem yapılamadı');
  }
}

const dateTime = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
};

/** Restoran kodu = şubenin AQUASOFT kodu (ör. "-OfYizc6pg0QZHZirhZZ"); sunucu şubeden kendisi alır. */
const branchCode = (branch?: any): string => (branch?.firebaseId || branch?.id || '').toString();

/**
 * Entegrasyonlar → Kurye Entegrasyonları: şube başına Fiyuu bağlantısı (ortam, restoran kodu, anahtar).
 * Kaydedilince firma başına tek "Fiyuu" kurye kaydı açılır; bildirim adresi Fiyuu'ya iletilir.
 */
const CourierIntegrationsSection: React.FC = () => {
  const { t } = useTranslation('salesSystem');
  const { branches, userDetails } = useAuth() as any;
  const companyName: string = userDetails?.companyName || userDetails?.company?.name || '';
  const [rows, setRows] = useState<CourierIntegration[]>([]);
  const [shared, setShared] = useState({ token: false, webhook: false });
  const [webhook, setWebhook] = useState<FiyuuWebhookInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [saved, setSaved] = useState<CourierIntegration | null>(null);
  const [eventsFor, setEventsFor] = useState<CourierIntegration | null>(null);
  const [events, setEvents] = useState<IntegrationEvent[]>([]);
  const [openPayload, setOpenPayload] = useState<number | null>(null);
  const [simFor, setSimFor] = useState<CourierIntegration | null>(null);
  const [simOrders, setSimOrders] = useState<OpenOrder[]>([]);
  const [sim, setSim] = useState({ externalOrderId: '', status: 'order-assigned', riderName: '', reason: '' });
  const [simBusy, setSimBusy] = useState(false);
  const [simResult, setSimResult] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const branchList = useMemo(() => (branches || []) as any[], [branches]);
  const branchName = useCallback(
    (id: string) => branchList.find((b) => b.id === id || b.firebaseId === id)?.name || id,
    [branchList],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await call<CourierIntegrationList>(mainApi.get(BASE));
      setRows(list.integrations);
      setShared({ token: list.sharedTokenConfigured, webhook: list.webhookConfigured });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Fiyuu bildirim adresi yalnız süper admine döner (gizli anahtar içerir); diğerlerinde sessizce gizlenir
  useEffect(() => {
    mainApi.get(`${BASE}/fiyuu-webhook`)
      .then((res) => setWebhook(res.data?.success ? res.data.data : null))
      .catch(() => setWebhook(null));
  }, []);

  const openNew = () => {
    const used = new Set(rows.map((r) => r.branchId));
    const first = branchList.find((b) => !used.has(b.id));
    setShowToken(false);
    setDraft({
      provider: 'FIYUU',
      branchId: first?.id || '',
      environment: 'test',
      storeId: branchCode(first),
      token: '',
      clearToken: false,
      hasOwnToken: false,
      isEnabled: true,
    });
  };

  const openEdit = (r: CourierIntegration) => {
    setShowToken(false);
    setDraft({
      id: r.id, provider: r.provider, branchId: r.branchId, environment: r.environment, storeId: r.storeId,
      token: '', clearToken: false, hasOwnToken: r.hasOwnToken, isEnabled: r.isEnabled,
    });
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const result = await call<CourierIntegration>(mainApi.post(BASE, {
        id: draft.id ?? null,
        provider: draft.provider,
        branchId: draft.branchId,
        environment: draft.environment,
        token: draft.token.trim() || null,
        clearToken: draft.clearToken,
        isEnabled: draft.isEnabled,
      }));
      setDraft(null);
      setSaved(result);
      await load();
    } catch (e: any) {
      setSnack({ message: e.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const test = async (r: CourierIntegration) => {
    setBusyId(r.id);
    try {
      const res = await call<{ ok: boolean; message: string }>(mainApi.post(`${BASE}/${r.id}/test`, {}));
      setSnack({ message: res.message, severity: res.ok ? 'success' : 'error' });
      await load();
    } catch (e: any) {
      setSnack({ message: e.message, severity: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const showEvents = async (r: CourierIntegration) => {
    setEventsFor(r);
    setEvents([]);
    setOpenPayload(null);
    try {
      setEvents(await call<IntegrationEvent[]>(mainApi.get(`${BASE}/${r.id}/events`)));
    } catch (e: any) {
      setSnack({ message: e.message, severity: 'error' });
    }
  };

  const openSim = async (r: CourierIntegration) => {
    setSimFor(r);
    setSimResult(null);
    setSim({ externalOrderId: '', status: 'order-assigned', riderName: '', reason: '' });
    try {
      const list = await call<OpenOrder[]>(mainApi.get(`${BASE}/${r.id}/open-orders`));
      setSimOrders(list);
      if (list.length > 0) setSim((s) => ({ ...s, externalOrderId: list[0].externalOrderId }));
    } catch (e: any) {
      setSimOrders([]);
      setSnack({ message: e.message, severity: 'error' });
    }
  };

  const runSim = async () => {
    if (!simFor || !sim.externalOrderId) return;
    setSimBusy(true);
    setSimResult(null);
    try {
      const res = await call<{ result: string }>(mainApi.post(`${BASE}/${simFor.id}/simulate`, {
        externalOrderId: sim.externalOrderId,
        status: sim.status,
        riderName: sim.riderName || null,
        reason: sim.reason || null,
      }));
      setSimResult(t(`integrationPage.courier.results.${res.result}`, { defaultValue: res.result }));
      const list = await call<OpenOrder[]>(mainApi.get(`${BASE}/${simFor.id}/open-orders`));
      setSimOrders(list);
    } catch (e: any) {
      setSimResult(e.message);
    } finally {
      setSimBusy(false);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnack({ message: t('integrationPage.courier.copied'), severity: 'success' });
    } catch {
      setSnack({ message: t('integrationPage.courier.copyFailed'), severity: 'error' });
    }
  };

  /** Fiyuu satış destek ekibine iletilecek metin: restoran ve restoran kodu. */
  const handoffText = (r: CourierIntegration) => [
    `${t('integrationPage.courier.handoff.restaurant')}: ${[companyName, r.branchName || branchName(r.branchId)].filter(Boolean).join(' · ')}`,
    `storeId: ${r.storeId}`,
    `${t('integrationPage.courier.handoff.environment')}: ${r.environment === 'live' ? 'Production' : 'Development'}`,
  ].join('\n');

  const webhookText = (w: FiyuuWebhookInfo) => [
    `${t('integrationPage.courier.handoff.webhook')} (${w.method}):`,
    ...w.statuses.map((s) => `  ${s}: ${w.url}`),
  ].join('\n');

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={600}>{t('integrationPage.courier.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('integrationPage.courier.subtitle')}</Typography>
        </Box>
        <Button variant="contained" startIcon={<DeliveryDiningIcon />} onClick={openNew}>{t('integrationPage.courier.add')}</Button>
      </Stack>

      {webhook && (
        <Alert severity={webhook.configured ? 'info' : 'warning'} sx={{ mb: 2 }}
          action={webhook.configured && webhook.url ? (
            <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copy(webhookText(webhook))}>{t('integrationPage.courier.copyAll')}</Button>
          ) : undefined}>
          <Typography variant="body2" fontWeight={600}>{t('integrationPage.courier.superAdminWebhook')}</Typography>
          {webhook.configured
            ? <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{webhook.url}</Typography>
            : <Typography variant="body2">{t('integrationPage.courier.webhookNotConfigured')}</Typography>}
        </Alert>
      )}
      {!shared.webhook && !webhook && (
        <Alert severity="info" sx={{ mb: 2 }}>{t('integrationPage.courier.webhookPending')}</Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}
      {!loading && rows.length === 0 && !error && (
        <Alert severity="info">{t('integrationPage.courier.empty')}</Alert>
      )}

      <Stack spacing={1.5}>
        {rows.map((r) => (
          <Card key={r.id} variant="outlined">
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, '&:last-child': { pb: 2 } }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip size="small" color="warning" label="Fiyuu" />
                <Typography fontWeight={600}>{r.branchName || branchName(r.branchId)}</Typography>
                <Chip size="small" variant="outlined" label={`storeId: ${r.storeId}`} />
                <Chip size="small" color={r.environment === 'live' ? 'success' : 'default'}
                  label={r.environment === 'live' ? t('integrationPage.courier.live') : t('integrationPage.courier.test')} />
                <Chip size="small" color={r.isEnabled ? 'success' : 'default'} variant={r.isEnabled ? 'filled' : 'outlined'}
                  label={r.isEnabled ? t('integrationPage.courier.enabled') : t('integrationPage.courier.disabled')} />
                <Chip size="small" variant="outlined"
                  label={r.hasOwnToken ? t('integrationPage.courier.ownToken') : t('integrationPage.courier.sharedToken')} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {t('integrationPage.courier.lastTest')}: {r.lastTestAt ? `${dateTime(r.lastTestAt)} · ${r.lastTestResult || ''}` : '—'}
                {' · '}
                {t('integrationPage.courier.lastEvent')}: {dateTime(r.lastEventAt)}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button size="small" variant="outlined" disabled={busyId === r.id || (!r.hasOwnToken && !shared.token)} onClick={() => test(r)}>
                  {busyId === r.id ? <CircularProgress size={16} /> : t('integrationPage.courier.test_')}
                </Button>
                <Button size="small" onClick={() => openEdit(r)}>{t('integrationPage.courier.edit')}</Button>
                <Button size="small" onClick={() => setSaved(r)}>{t('integrationPage.courier.handoffButton')}</Button>
                <Button size="small" onClick={() => showEvents(r)}>{t('integrationPage.courier.events')}</Button>
                {r.environment === 'test' && (
                  <Button size="small" onClick={() => openSim(r)}>{t('integrationPage.courier.simulate')}</Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Ekle / düzenle */}
      <Dialog open={!!draft} onClose={() => !saving && setDraft(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{draft?.id ? t('integrationPage.courier.editTitle') : t('integrationPage.courier.add')}</DialogTitle>
        <DialogContent dividers>
          {draft && (
            <Stack spacing={2}>
              <TextField select size="small" label={t('integrationPage.courier.provider')} value={draft.provider}
                disabled={!!draft.id} onChange={(e) => setDraft({ ...draft, provider: e.target.value })}>
                <MenuItem value="FIYUU">Fiyuu</MenuItem>
                <MenuItem value="ZIRVEGO" disabled>{`Zirve Go · ${t('integrationPage.courier.docPending')}`}</MenuItem>
              </TextField>
              <TextField select size="small" label={t('integrationPage.courier.branch')} value={draft.branchId}
                onChange={(e) => {
                  const b = branchList.find((x) => x.id === e.target.value);
                  setDraft({ ...draft, branchId: e.target.value, storeId: branchCode(b) });
                }}>
                {branchList.map((b) => (
                  <MenuItem key={b.id} value={b.id} disabled={rows.some((r) => r.branchId === b.id && r.id !== draft.id)}>{b.name}</MenuItem>
                ))}
              </TextField>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('integrationPage.courier.environment')}</Typography>
                <ToggleButtonGroup size="small" exclusive fullWidth value={draft.environment}
                  onChange={(_, v) => { if (v) setDraft({ ...draft, environment: v }); }}>
                  <ToggleButton value="test">{t('integrationPage.courier.test')}</ToggleButton>
                  <ToggleButton value="live">{t('integrationPage.courier.live')}</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <TextField size="small" label={t('integrationPage.courier.storeId')} value={draft.storeId}
                InputProps={{ readOnly: true }} helperText={t('integrationPage.courier.storeIdHint')} />
              <TextField size="small" label={t('integrationPage.courier.token')} value={draft.token}
                type={showToken ? 'text' : 'password'} autoComplete="new-password"
                placeholder={draft.hasOwnToken ? t('integrationPage.courier.tokenKeep') : shared.token ? t('integrationPage.courier.tokenShared') : ''}
                onChange={(e) => setDraft({ ...draft, token: e.target.value, clearToken: false })}
                helperText={shared.token ? t('integrationPage.courier.tokenHintOptional') : t('integrationPage.courier.tokenHint')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowToken((v) => !v)}>
                        {showToken ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }} />
              {draft.hasOwnToken && shared.token && (
                <FormControlLabel control={<Switch checked={draft.clearToken} onChange={(e) => setDraft({ ...draft, clearToken: e.target.checked, token: '' })} />}
                  label={t('integrationPage.courier.useSharedToken')} />
              )}
              <FormControlLabel control={<Switch checked={draft.isEnabled} onChange={(e) => setDraft({ ...draft, isEnabled: e.target.checked })} />}
                label={draft.isEnabled ? t('integrationPage.courier.enabled') : t('integrationPage.courier.disabled')} />
              <Alert severity="info">{t('integrationPage.courier.courierNote')}</Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setDraft(null)}>{t('integrationPage.courier.cancel')}</Button>
          <Button variant="contained"
            disabled={saving || !draft?.branchId
              || (!shared.token && !draft?.hasOwnToken && !draft?.token.trim())}
            onClick={save}>
            {saving ? <CircularProgress size={18} color="inherit" /> : t('integrationPage.courier.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fiyuu'ya iletilecek bilgiler */}
      <Dialog open={!!saved} onClose={() => setSaved(null)} maxWidth="md" fullWidth>
        <DialogTitle>{t('integrationPage.courier.handoffTitle')}</DialogTitle>
        <DialogContent dividers>
          {saved && (
            <Stack spacing={1.5}>
              <Typography variant="body2">{t('integrationPage.courier.handoffIntro')}</Typography>
              <Box component="pre" sx={{ m: 0, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {handoffText(saved)}
              </Box>
              <Alert severity="warning">{t('integrationPage.courier.handoffSecret')}</Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {saved && <Button startIcon={<ContentCopyIcon />} onClick={() => copy(handoffText(saved))}>{t('integrationPage.courier.copyAll')}</Button>}
          <Button variant="contained" onClick={() => setSaved(null)}>{t('integrationPage.courier.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Gelen bildirimler */}
      <Dialog open={!!eventsFor} onClose={() => setEventsFor(null)} maxWidth="md" fullWidth>
        <DialogTitle>{t('integrationPage.courier.eventsTitle', { branch: eventsFor ? (eventsFor.branchName || branchName(eventsFor.branchId)) : '' })}</DialogTitle>
        <DialogContent dividers>
          {events.length === 0 && <Typography variant="body2" color="text.secondary">{t('integrationPage.courier.noEvents')}</Typography>}
          {events.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('integrationPage.courier.cols.time')}</TableCell>
                  <TableCell>{t('integrationPage.courier.cols.order')}</TableCell>
                  <TableCell>{t('integrationPage.courier.cols.status')}</TableCell>
                  <TableCell>{t('integrationPage.courier.cols.rider')}</TableCell>
                  <TableCell>{t('integrationPage.courier.cols.result')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((e) => (
                  <React.Fragment key={e.id}>
                    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setOpenPayload(openPayload === e.id ? null : e.id)}>
                      <TableCell>{dateTime(e.receivedAt)}</TableCell>
                      <TableCell>{e.externalOrderId || '—'}</TableCell>
                      <TableCell>{e.status ? t(`integrationPage.courier.statuses.${e.status}`, { defaultValue: e.status }) : '—'}</TableCell>
                      <TableCell>{e.riderName || '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" color={e.result === 'failed' ? 'error' : e.result === 'applied' ? 'success' : 'default'}
                          label={t(`integrationPage.courier.results.${e.result}`, { defaultValue: e.result })} />
                        {e.note && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{e.note}</Typography>}
                      </TableCell>
                    </TableRow>
                    {openPayload === e.id && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{e.payloadJson}</Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventsFor(null)}>{t('integrationPage.courier.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Bildirim dene (yalnız test ortamı) */}
      <Dialog open={!!simFor} onClose={() => !simBusy && setSimFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('integrationPage.courier.simulateTitle')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">{t('integrationPage.courier.simulateIntro')}</Typography>
            {simOrders.length === 0 && <Alert severity="info">{t('integrationPage.courier.simulateNoOrders')}</Alert>}
            {simOrders.length > 0 && (
              <TextField select size="small" label={t('integrationPage.courier.cols.order')} value={sim.externalOrderId}
                onChange={(e) => setSim({ ...sim, externalOrderId: e.target.value })}>
                {simOrders.map((o) => (
                  <MenuItem key={o.deliveryId} value={o.externalOrderId}>
                    {`${o.orderNumber || o.externalOrderId} · ${o.externalStatus ? t(`integrationPage.courier.statuses.${o.externalStatus}`, { defaultValue: o.externalStatus }) : o.status}${o.riderName ? ` · ${o.riderName}` : ''}`}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField select size="small" label={t('integrationPage.courier.cols.status')} value={sim.status}
              onChange={(e) => setSim({ ...sim, status: e.target.value })}>
              {SIM_STATUSES.map((s) => <MenuItem key={s} value={s}>{t(`integrationPage.courier.statuses.${s}`)}</MenuItem>)}
            </TextField>
            {sim.status !== 'order-created' && sim.status !== 'order-canceled' && (
              <TextField size="small" label={t('integrationPage.courier.cols.rider')} value={sim.riderName}
                onChange={(e) => setSim({ ...sim, riderName: e.target.value })} />
            )}
            {sim.status === 'order-canceled' && (
              <TextField size="small" label={t('integrationPage.courier.simulateReason')} value={sim.reason}
                onChange={(e) => setSim({ ...sim, reason: e.target.value })} />
            )}
            {simResult && <Alert severity="info">{t('integrationPage.courier.simulateResult', { result: simResult })}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={simBusy} onClick={() => setSimFor(null)}>{t('integrationPage.courier.close')}</Button>
          <Button variant="contained" disabled={simBusy || !sim.externalOrderId} onClick={runSim}>
            {simBusy ? <CircularProgress size={18} color="inherit" /> : t('integrationPage.courier.simulateSend')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={6000} onClose={() => setSnack(null)}>
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default CourierIntegrationsSection;
